import React, { useState, useCallback, useEffect } from 'react';
import { Typography, Divider, Paper, Grid, Box, Button, FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getRoles } from 'services/roles';
import { getPermissions, savePermissions } from 'services/permissions';
import ContextSelection from 'components/contextSelection';

const PermissionsPage = () => {

  const [ contextSelected, setContext] = React.useState({});
  const [ selectedRole, setSelectedRole] = React.useState('');
  const [ permissions, setPermissions] = React.useState([]);
  const [ selectedPermissions, setSelectedPermissions] = React.useState([]);

  const [roles, setRoles] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const clearPermissions = useCallback(() => {
    setSelectedPermissions([]);
    setPermissions([])
  }, []);

  const getRolesData = useCallback((context) => {
    getRoles(null, null, `/${context}`).then(({response, error}) => {
      if(error){
        setRoles([])
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      } else {
        setSelectedRole('');
        clearPermissions();
        setRoles(response);
      }
    });
  }, [enqueueSnackbar, clearPermissions]);

  const onContextSelected = useCallback((context) => {
    if(contextSelected !== context){
      setContext(context);
      getRolesData(context);
    }
  }, [contextSelected, getRolesData]);

  function onGoBtnClick() {
    if(!contextSelected){
      enqueueSnackbar("Select context", {variant: 'error'});
      return;
    }
    if(!selectedRole){
      enqueueSnackbar("Select role", {variant: 'error'});
      return;
    }
    getPermissionData();
  }

  function getPermissionData() {
    const data = 
    { context: contextSelected,
      roleId: selectedRole
    };
    getPermissions({...data}).then(({response, error}) => {
      if(error){

        setPermissions(null);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      } else {
        const allowedPermissions = response.allowedPermissions.reduce(function (rows, key, index) { 
          return (index % 6 === 0 ? rows.push([key]) 
            : rows[rows.length-1].push(key)) && rows;
        }, []);
        setPermissions([...allowedPermissions]);
        setSelectedPermissions([...response.permissions]);
      }
    });
  }

  function handleSelect(checkedName) {
    const newNames = selectedPermissions?.includes(checkedName)
      ? selectedPermissions?.filter(name => name !== checkedName)
      : [...(selectedPermissions ?? []), checkedName];
    setSelectedPermissions(newNames);
    return newNames;
  }

  function onSaveBtnClick() {
    const postData = {
      roleId: selectedRole,
      permissions: selectedPermissions,
      context: contextSelected
    }
    savePermissions({...postData}).then(({response, error}) => {
      if(error){
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      } else {
      enqueueSnackbar((response?.message || 'Permissions saved successfully'), {variant: 'success'});
        clearSelection()
      }
    });
  }

  useEffect(() => {
    clearPermissions()
  }, [selectedRole, clearPermissions])
  
  function clearSelection(){
    clearPermissions();
    selectedRole('');
  }

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Permissions
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container>
          <Grid container item xs={12} sm={12} md={12} xl={12} lg={12} sx={{p:1, }}>
            <Grid item sx={{pt: 2,  pl:1}} xs={12} sm={3} md={3} xl={3} lg={3}>
              <ContextSelection onContextSelected={onContextSelected} defaultContext='ADMIN' variant="outlined"/>
            </Grid>
            <Grid item sx={{pt: 2,  pl:1}} xs={12} sm={4} md={5} xl={3} lg={4}>
            <FormControl fullWidth>
              <InputLabel size="small" id="role-select-label">Role</InputLabel>
              <Select 
                size="small"
                labelId="role-select-label"
                value={selectedRole}
                label="Role"
                defaultValue=''
                onChange={(e) => setSelectedRole(e.target.value)}
                sx={{ padding: '4px 6px', fontSize: 14 }}
              >
                {roles.map(({role, roleId}, index) => <MenuItem key={roleId} value={roleId}>{role}</MenuItem>)}
              </Select>
            </FormControl>
            </Grid>
            <Grid item sx={{pt: 2,  pl:2}} xs={12} sm={5} md={4} xl={4} lg={4}>
              <Button variant="contained" onClick={onGoBtnClick} sx={{py: '8px', fontSize: 14, textTransform: 'none'}}>Get Permissions</Button>
            </Grid>
          </Grid>
        </Grid>
         
        {permissions.length ? (
          <Grid container item xs={12} sm={12} md={12} lg={12} >
            <Grid item sx={{pt: 2,  pl:2}} xs={12} sm={12} md={12} xl={12} lg={12}>
              <Typography component="h2" variant="subtitle1"  gutterBottom >
                Select Permissions
              </Typography>
            </Grid>
              {permissions.map((permissionsArr, index) => (
                <Grid key={index} item sx={{pt:1, pl:2}} xs={12} sm={6} md={4} xl={3} lg={4}>
                  <FormGroup>
                  {permissionsArr.map((permission, index1) => (
                      <FormControlLabel
                        key={index1}
                        control={
                          <Checkbox
                            checked={selectedPermissions.includes(permission)}
                            onChange={() => handleSelect(permission)}
                          />
                        }
                        label={permission}
                      />
                  ))}
                  </FormGroup>
              </Grid>
            ))}
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                sx={{ mt: 3, mb: 2, textTransform: 'inherit' }}
                color="success"
                onClick={onSaveBtnClick}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        ): null}
      </Box> 
    </Paper>
  );
}
 
export default PermissionsPage;
